import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function conversationKey(task) {
  return `${task.type}:${task.project_id || ""}:${task.id}`;
}

function conversationPath(task) {
  const params = new URLSearchParams({ conversation: `${task.type}:${task.id}` });
  if (task.type === "report") params.set("project_id", task.project_id);
  return `/tasks?${params}`;
}

export default function TaskConversationAlerts({ conversations, userId, alertsEnabled = true }) {
  const [open, setOpen] = useState(false);
  const previousMessages = useRef(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const unread = useMemo(() => (conversations || []).filter((task) => task.conversation_unread_count > 0), [conversations]);
  const count = unread.reduce((total, task) => total + task.conversation_unread_count, 0);

  useEffect(() => {
    if (!conversations) return;
    const next = new Map(conversations.map((task) => [conversationKey(task), task.conversation_last_message?.id]));
    const params = new URLSearchParams(search);
    if (previousMessages.current && alertsEnabled && !open) {
      for (const task of unread) {
        const last = task.conversation_last_message;
        const viewing = pathname === "/tasks" && params.get("conversation") === `${task.type}:${task.id}`
          && (task.type !== "report" || params.get("project_id") === task.project_id);
        if (!last?.id || last.user_id === userId || viewing || previousMessages.current.get(conversationKey(task)) === last.id) continue;
        toast.message(`${last.user_name || "A participant"}: ${task.title || "Task conversation"}`, {
          id: `task-message-${conversationKey(task)}`,
          description: (last.message || "New task message").slice(0, 160),
          action: { label: "Open", onClick: () => navigate(conversationPath(task)) },
        });
      }
    }
    previousMessages.current = next;
  }, [conversations, unread, userId, alertsEnabled, navigate, pathname, search, open]);

  const onOpenChange = (nextOpen) => {
    setOpen(nextOpen);
    if (nextOpen) {
      for (const task of conversations || []) toast.dismiss(`task-message-${conversationKey(task)}`);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon"
          className="relative h-9 w-9 sm:h-10 sm:w-10 text-[#647069] hover:text-[#16201A] dark:text-white/60 dark:hover:text-white hover:bg-[#F7F8F6] dark:hover:bg-white/10"
          data-testid="task-message-alerts-button"
          aria-label={alertsEnabled && count > 0 ? `Task messages (${count} unread)` : "Task messages"}
          title="Task messages">
          <MessageSquare className="h-5 w-5" />
          {alertsEnabled && count > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#B85C42] px-1 text-[10px] font-bold text-white"
            data-testid="task-message-alert-count" aria-hidden="true">{count > 99 ? "99+" : count}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={12} className="w-[calc(100vw-1.5rem)] max-w-sm p-0" data-testid="task-message-alerts-popover">
        <h2 className="border-b px-4 py-3 text-sm font-semibold">Task Messages{count > 0 ? ` (${count} unread)` : ""}</h2>
        <div className="max-h-[min(24rem,calc(100dvh-10rem))] overflow-y-auto">
          {unread.length === 0 ? <p className="p-4 text-sm text-muted-foreground" role="status">No unread task messages.</p>
            : unread.map((task) => <button key={conversationKey(task)} type="button"
              className="flex w-full min-w-0 items-start gap-3 border-b p-4 text-left hover:bg-muted/50"
              data-testid={`task-message-alert-${task.type}-${task.id}`}
              onClick={() => { setOpen(false); navigate(conversationPath(task)); }}>
              <div className="min-w-0 flex-1">
                {(task.project_number || task.project_name || task.prospective_project_name) && <p className="break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">
                  {task.project_number ? `#${task.project_number} - ` : ""}{task.project_name || task.prospective_project_name}
                </p>}
                <p className="break-words text-sm font-semibold [overflow-wrap:anywhere]">{task.title || "Task"}{task.phase ? ` - ${task.phase}` : ""}</p>
                <p className="mt-1 line-clamp-2 break-words text-xs text-muted-foreground [overflow-wrap:anywhere]">{task.conversation_last_message?.user_name}: {task.conversation_last_message?.message}</p>
                {task.conversation_last_message?.created_at && <time className="mt-1 block text-xs text-muted-foreground" dateTime={task.conversation_last_message.created_at}>
                  {new Date(task.conversation_last_message.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </time>}
              </div>
              <span className="shrink-0 rounded-full bg-[#B85C42] px-2 py-0.5 text-xs font-semibold text-white" aria-label={`${task.conversation_unread_count} unread messages`}>
                {task.conversation_unread_count > 99 ? "99+" : task.conversation_unread_count}
              </span>
            </button>)}
        </div>
        <Button variant="ghost" className="w-full rounded-none text-sm" onClick={() => { setOpen(false); navigate("/tasks"); }}>View Tasks</Button>
      </PopoverContent>
    </Popover>
  );
}
